import React from 'react';
import cn from 'classnames';
import { SVG, TSvg } from '@mayfield/common/components';
import { TThemeColor } from '@mayfield/sanity';
import * as styles from './styles.module.scss';

type TInputTypes = 'text' | 'password' | 'number';

interface IProps {
  placeholder?: string;
  id?: string;
  value?: string;
  label?: string;
  hint?: string;
  type?: TInputTypes;
  warningMessage?: string;
  iconLeft?: TSvg;
  hasError?: boolean;
  required?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  themeColor?: TThemeColor;
  onChange?: (value: string) => void;
  className?: string;
  textarea?: boolean;
  onBlur?: () => void;
}

const TextInput = ({
  placeholder,
  id,
  value,
  label,
  hint,
  type = 'text',
  warningMessage,
  iconLeft,
  hasError,
  themeColor = 'sea',
  required,
  disabled,
  onClick,
  onChange,
  className,
  textarea,
  onBlur
}: IProps) => {
  const MAX_LENGTH = 300;

  const handleChange:
    | (React.ChangeEventHandler<HTMLTextAreaElement> &
        React.ChangeEventHandler<HTMLInputElement>)
    | undefined = (e) => {
    if (onChange) onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!onClick) return;
    if (e.key === 'Enter') onClick();
  };

  const InputElement = textarea ? 'textarea' : 'input';

  const isButtonDisabled = disabled || hasError || !value;

  return (
    <div
      className={cn(styles.container, className, {
        [styles.error]: hasError,
        [styles.disabled]: disabled,
        [styles.hasIcon]: iconLeft,
        [styles.hasArrow]: onClick
      })}
      style={{ color: `var(--color-${themeColor}-mid)` }}
    >
      {(label || hint || required) && (
        <div className={styles.topText}>
          <div>
            {(label || required) && (
              <label className={cn('caption', styles.label)} htmlFor={id}>
                {required && '* '}
                {label}
              </label>
            )}
          </div>

          <div>{hint && <span className={cn('caption')}>{hint}</span>}</div>
        </div>
      )}

      <div className={styles.inputContainer}>
        {iconLeft && !textarea && (
          <div className={styles.iconContainer}>
            <SVG className={styles.icon} svg={iconLeft} />
          </div>
        )}

        <InputElement
          maxLength={MAX_LENGTH}
          tabIndex={disabled ? -1 : 0}
          id={id}
          className={cn('b2', styles.input, {
            [styles.textarea]: textarea,
            [styles.hasIcon]: iconLeft && !textarea,
            [styles.hasArrow]: onClick,
            [styles.password]: type === 'password' && value
          })}
          type={type}
          placeholder={placeholder}
          onChange={handleChange}
          value={value}
          onKeyDown={handleKeyDown}
          onBlur={() => onBlur && onBlur()}
        />

        {onClick && (
          <div className={styles.buttonContainer}>
            <button
              type="submit"
              aria-label="Submit"
              onClick={(e) => {
                e.preventDefault();
                onClick();
              }}
              className={styles.button}
              disabled={isButtonDisabled}
            >
              <SVG className={styles.icon} svg="arrowRight" />
            </button>
          </div>
        )}
      </div>

      {warningMessage && hasError && (
        <span className={cn('caption', styles.warning)}>
          <SVG svg="warningTriangle" className={styles.icon} />
          {warningMessage}
        </span>
      )}
    </div>
  );
};

export default TextInput;
